<script setup lang="ts">
import { vipTierStyle } from '@/utils/vip-tiers';

// Card da roletinha nas versões de VIP — composto em DOM, não uma imagem
// exportada. Ele NÃO conhece store nem contrato: recebe o tier e os textos por
// prop, então sobe sozinho num harness (`tier=0..6`) sem Pinia e sem
// `window._smartico`. Quem cruza o status do usuário com o contrato do hub é o
// Card.vue.
//
// Por que DOM e não imagem: com imagem, cada tier eram dois arquivos
// (mobile + desktop, 14 no total), o texto ficava assado (sem acessibilidade e
// sem poder mudar copy sem re-export) e a geometria de bleed/canto do build
// script esculpia um canto arredondado no meio do card — a "mordida" da borda.
//
// Medidas: Figma node 21807:67933 (mobile, card 374 × 88) e 21791:67598
// (desktop, 551,5 × 164). Os dois desenhos NÃO são um do outro em escala — o
// mobile põe o badge na MESMA LINHA do título e usa outro ângulo de gradiente —,
// então cada breakpoint tem seu próprio bloco de medidas. O DOM é o mesmo; muda
// só o CSS.
const props = defineProps<{
  tier: number;
  // Textos vêm de fora (contrato do hub) — o componente não decide copy.
  // `title` é o texto SEM o tier: o "VIP 3" é acrescentado a partir da tabela,
  // pra não depender de o contrato repetir o número em sete lugares.
  title: string;
  subtitle: string;
  cta: string;
}>();

const emit = defineEmits<{ (e: 'select'): void }>();

const style = computed(() => vipTierStyle(props.tier));

// "Roleta Diária" + "VIP 3". No tier 0 o label é só "VIP" (decisão do design),
// então a mesma concatenação já resolve os dois casos.
const heading = computed(() =>
  style.value ? `${props.title} ${style.value.label}` : props.title,
);
</script>

<template>
  <button
    v-if="style"
    class="rvc"
    :style="{ '--rvc-from': style.from, '--rvc-to': style.to }"
    type="button"
    @click="emit('select')"
    @dragstart.prevent
  >
    <!-- A roda sangra acima do corpo (é assim no Figma) e precisa ser cortada
         pelo canto arredondado embaixo à direita. Quem faz esse corte é esta
         caixa, não o card: o card não pode ter `overflow: hidden`, senão come
         justamente a sangria de cima. -->
    <span class="rvc__art">
      <img
        :alt="''"
        aria-hidden="true"
        class="rvc__wheel"
        decoding="async"
        loading="lazy"
        :src="style.wheel"
      />
    </span>

    <!-- Um DOM só pros dois breakpoints: no mobile `__head` é uma linha (badge ao
         lado do título) e no desktop vira coluna (badge acima). -->
    <span class="rvc__content">
      <span class="rvc__text">
        <span class="rvc__head">
          <img
            :alt="''"
            aria-hidden="true"
            class="rvc__badge"
            decoding="async"
            loading="lazy"
            :src="style.badge"
          />

          <span class="rvc__title">{{ heading }}</span>
        </span>

        <span class="rvc__subtitle">{{ subtitle }}</span>
      </span>

      <span class="rvc__cta">{{ cta }}</span>
    </span>
  </button>
</template>

<style scoped lang="scss">
// TODAS as medidas em `cqw` — 1cqw = 1% da LARGURA DO CARD, ou seja 3,74px no
// desenho mobile e 5,515px no desktop. Cada valor é `medida_do_figma / largura`.
// Motivo de não usar `%`: dentro da coluna a porcentagem passa a valer sobre a
// caixa dela e não sobre o card — foi o que empurrou o badge 10px pra esquerda na
// primeira versão. `cqw` sempre se refere ao card, em qualquer nível.
// Cada declaração `cqw` vem depois de uma em `px` (o valor no desenho), que é o
// fallback pra navegador sem container query.

/* ================= MOBILE (base) — card 374 × 88 ================= */
.rvc {
  container-type: inline-size;
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  aspect-ratio: 374 / 88;
  // ATENÇÃO: nada de `cqw` NESTE seletor. Um elemento não consulta a si mesmo —
  // `cqw` aqui resolve contra o container ANCESTRAL mais próximo (e, não havendo
  // nenhum, contra a viewport). Um `padding-left: 2.9412cqw` aqui virava 35px numa
  // viewport de 1200 em vez de 11px. Pior: como `cqw` dos descendentes é 1% da
  // CONTENT-BOX do container, qualquer padding aqui encolhe a régua de todo o
  // resto. Por isso o recuo lateral vive no `__content`, que é descendente.
  padding: 0;
  // Sem borda: o Figma tem um traço de `rgba(255,255,255,.05)` em volta do card,
  // mas na tela ele só suja o contorno contra a névoa do painel. Decisão do time.
  border: 0;
  border-radius: 12px;
  // O ângulo é diferente por breakpoint (128,6° no mobile, 122,28° no desktop),
  // então o gradiente vive no CSS e o componente só injeta as duas cores.
  background-image: linear-gradient(
    128.6deg,
    var(--rvc-from) 7.99%,
    var(--rvc-to) 106.91%
  );
  // A roda passa do topo — o card não pode cortar.
  overflow: visible;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;

  &:active {
    filter: brightness(0.94);
  }
}

// Caixa da roda: 132 de largura (35,2941%), do topo −6,5 (−7,3864% de 88) até a
// base do card. O canto arredondado sai daqui, e NÃO assado no `.webp`: o arquivo
// é retangular e o corte é do navegador, que é o único jeito de o raio acompanhar
// o card se ele mudar de tamanho.
.rvc__art {
  position: absolute;
  top: -7.3864%;
  right: 0;
  bottom: 0;
  width: 35.2941%;
  overflow: hidden;
  border-bottom-right-radius: 12px;
  // Decoração não participa do hit-test: sem isto a faixa que sobe além do card
  // pairaria sobre o card anterior e roubaria o clique dele (o mesmo bug que o
  // Card.vue já resolveu na arte estática).
  pointer-events: none;
}

.rvc__wheel {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
}

// Coluna "Text+cta": 66 de altura, título/subtítulo no topo e CTA na base.
.rvc__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  // `flex-start` e não o `stretch` padrão: item de flex-column estica no eixo
  // cruzado, e o badge (que tem só altura definida, largura pela proporção da
  // arte) sairia esmagado.
  align-items: flex-start;
  justify-content: space-between;
  height: 66px;
  height: 17.6471cqw;
  // O recuo lateral do card (11 no mobile) mora aqui — ver a nota em `.rvc`.
  margin-left: 11px;
  margin-left: 2.9412cqw;
}

// Bloco de texto: 36 de altura, com a linha do título no topo e o subtítulo na
// base.
.rvc__text {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 36px;
  height: 9.6257cqw;
}

// Mobile: badge e título na MESMA linha.
// Altura FIXA de 18 e `flex-start`, como no Figma: sem isso a linha cresce pro
// tamanho da caixa de texto do título (20px) e empurra o badge ~2px pra baixo.
.rvc__head {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  height: 18px;
  height: 4.8128cqw;
  // O Figma reserva uma caixa fixa de 43,219 pro badge e só 1px de gap; como a
  // arte de cada tier tem largura própria (a altura é que é fixa), o gap aqui faz
  // o papel dessa reserva. Variação entre tiers fica em ~1px.
  gap: 8px;
  gap: 2.139cqw;
}

// Altura do conteúdo do badge: 17,25 (a caixa do Figma é 17,748 e a arte ocupa
// 97,2% dela). Largura pela proporção da imagem — cada tier tem a sua.
.rvc__badge {
  width: auto;
  height: 17.25px;
  height: 4.6122cqw;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.rvc__title {
  color: #fff;
  font-size: 14px;
  font-size: 3.7433cqw;
  font-weight: 600;
  // 18 / 14 — a altura da LINHA do Figma. Ele reporta `leading: 20` numa linha de
  // 18, ou seja o texto transborda 1px pra cada lado; aqui a caixa é a linha.
  line-height: 1.2857;
  white-space: nowrap;
}

// O Figma escreve o subtítulo com três espaços na frente pra deslocá-lo; aqui
// isso é `padding`, porque o texto vem do contrato e não pode carregar espaço.
.rvc__subtitle {
  color: #fff;
  padding-left: 4.67px;
  padding-left: 1.2487cqw;
  font-size: 10px;
  font-size: 2.6738cqw;
  font-weight: 400;
  line-height: 1.26;
  white-space: nowrap;
}

// CTA: padding 5/12/6, borda 1, radius 16, 10px/600.
// É um <span> e não um <button>: o card inteiro já é o botão, e botão dentro de
// botão é HTML inválido (o navegador desmonta a árvore).
.rvc__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-width: 8.5561cqw;
  padding: 5px 12px 6px;
  padding: 1.3369cqw 3.2086cqw 1.6043cqw;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-width: 0.2674cqw;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: #fcfcfc;
  font-size: 10px;
  font-size: 2.6738cqw;
  font-weight: 600;
  // 1,15 e não 1: o Figma usa `leading: normal` no rótulo, o que dá ~24,3 de
  // altura no botão. Com `1` o botão sai 2px mais baixo que o desenho.
  line-height: 1.15;
  white-space: nowrap;
}

/* ================= DESKTOP — card 551,5 × 164 ================= */
// Aqui 1cqw = 5,515px. O breakpoint é o mesmo que o hub usa pra trocar a arte dos
// outros cards (<picture> em Card.vue).
@media (min-width: 1024px) {
  .rvc {
    aspect-ratio: 551.5 / 164;
    background-image: linear-gradient(
      122.28deg,
      var(--rvc-from) 7.99%,
      var(--rvc-to) 106.91%
    );
  }

  .rvc__art {
    top: -7.072%; // −11,598 / 164
    width: 44.3611%; // 244,651 / 551,5
  }

  // Desktop: a coluna toda é centrada na altura e o CTA fica 11,36 abaixo do
  // subtítulo (no mobile ele é empurrado pra base por `space-between`).
  .rvc__content {
    justify-content: center;
    gap: 11.36px;
    gap: 2.0598cqw;
    height: 100%;
    margin-left: 20px;
    margin-left: 3.6265cqw;
  }

  .rvc__text {
    // 286,85 e não os 269 do Figma: é a largura que sobra na coluna
    // (306,849 − 20 de padding), então usar tudo não invade nada e dá folga pro
    // texto não quebrar.
    width: 286.85px;
    width: 51.9946cqw;
    height: auto;
    justify-content: flex-start;
    gap: 6px;
    gap: 1.0879cqw;
  }

  // Desktop: badge ACIMA do título.
  // `height: auto` é obrigatório aqui: no mobile esta linha tem altura fixa de 18,
  // e mantê-la no desktop esmaga a coluna (badge + título somam ~56) e joga o CTA
  // pra fora do card.
  .rvc__head {
    flex-direction: column;
    align-items: flex-start;
    height: auto;
    gap: 6px;
    gap: 1.0879cqw;
  }

  .rvc__badge {
    height: 21.55px;
    height: 3.9075cqw;
  }

  .rvc__title {
    font-size: 22.45px;
    font-size: 4.0709cqw;
    // 28,865 / 22,451 — a ALTURA DA CAIXA do Figma, não o `leading` que ele
    // reporta (32,073). O Figma desenha o texto com leading maior dentro de uma
    // caixa menor; quem manda no empilhamento é a caixa. Usar 32,073 aqui inflava
    // o bloco em 3px e empurrava subtítulo e CTA pra baixo.
    line-height: 1.2857;
  }

  // 15px e não os 16,036 do Figma: com 16 o subtítulo quebrava em duas linhas no
  // app (no desenho ele fecha em 269px com 4px de folga — qualquer diferença de
  // renderização de fonte estoura).
  .rvc__subtitle {
    padding-left: 0;
    font-size: 15px;
    font-size: 2.7199cqw;
    line-height: 1.476;
  }

  .rvc__cta {
    min-width: 121.49px;
    min-width: 22.0284cqw;
    padding: 8.02px 19.24px 9.62px;
    padding: 1.4539cqw 3.4894cqw 1.7448cqw;
    border-width: 1.6px;
    border-width: 0.2909cqw;
    font-size: 16.04px;
    font-size: 2.9077cqw;
    // 16,79 / 16,036 — o que fecha os 37,64 de altura do botão no desenho
    // desktop (o mobile usa 1,15, que é a proporção do botão pequeno).
    line-height: 1.047;
  }
}
</style>
